'use client';

import { useState, useEffect, useRef } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import Icon from '@/components/ui/AppIcon';
import toast from 'react-hot-toast';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const WINGS_SYSTEM_PROMPT = `You are AskWings AI Bot — the official technical assistant for Wings Bharath (Halamanee Aerospace Group), an elite aerospace and defence manufacturing company based in India.

## Company Overview
Wings Bharath is an "Airbus-Approved Aerospace Precision Components Manufacturing Unit" — our name is listed in the official Airbus Supplier Portal. We hold an Official Defence Manufacturing Licence granted by the Ministry of Home Affairs (Arms Section), Government of India.

## Key Certifications & Approvals
- **Airbus Approved Supplier** — Cabin crew structural sheet metal components, A320 programme
- **Rafale Approved** — Class 2 and Class 3 parts
- **AS9100D Certified** — International Aerospace Quality Management Standard
- **ISMS, EHS & OHS Systems** — Data security, environmental health & safety
- **Defence Manufacturing Licence** — Ministry of Home Affairs, Government of India

## Programmes & Sectors

### Commercial Aircraft
- Airbus A320 programme — structural sheet metal components, cabin crew structural parts
- Rafale — Class 2 and Class 3 precision parts

### Defence
- LCA Tejas — structural components, window components, wing and fuselage components
- Su-30 MKI — structural and fuselage components
- UAVs — complete engine assembly execution
- Remote Weapon Systems — structural components
- Reverse Engineering Projects — executed successfully

### Space
- Satellite systems — structural support systems
- Launch vehicle structural components
- Satellite launcher structural support

### Robotic Arms
- Structural parts for robotic arm assemblies

## Primary Materials
Aluminium 6000 series, Aluminium 7000 series, Stainless Steel (SS), Titanium (Ti), Brass

## Manufacturing Capabilities

### 1. Complete In-House Manufacturing Ecosystem
Single seamless manufacturing pipeline — from precision structural component machining and sub-assemblies to complex sheet-metal capabilities. Eliminates multi-vendor inefficiencies with absolute control over quality, timelines, and traceability.

### 2. Advanced Multi-axis Machining Infrastructure
State-of-the-art DOOSAN, HAAS, and PVM machining centres with 3-axis, 4-axis, and 5-axis simultaneous machining capability. Handles intricate geometries and tight aerospace tolerances.

### 3. Precision Tool Room
Wire EDM, Sparking (EDM), high-accuracy tool-room operations, and special-purpose fabrication for the most demanding component requirements.

### 4. Sheet Metal Capabilities
Advanced sheet-metal facility approved by Airbus — structural sheet metal components for aerospace and defence programmes.

### 5. Digital Backbone
Dedicated ERP system, secure cloud architecture, and secure FTP systems ensuring full traceability, documentation security, and real-time process control — critical for Aerospace & Defence programmes.

## Contact
- Email: info@wingsbharath.com
- Emergency Hotline: +91 9900008890

## Your Role
Answer technical questions about Wings Bharath's capabilities, certifications, programmes, materials, and manufacturing infrastructure. Be professional, precise, and helpful. For RFQs or detailed project discussions, guide clients to contact info@wingsbharath.com or call +91 9900008890. Keep responses concise and technical. Use bullet points for clarity when listing capabilities.`;

interface AskWingsBotProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AskWingsBot({ isOpen, onClose }: AskWingsBotProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [notified, setNotified] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', true);

  useEffect(() => {
    if (error) toast.error('Bot error. Please try again.');
  }, [error]);

  // Append assistant response when streaming completes
  const prevIsLoading = useRef(false);
  useEffect(() => {
    if (prevIsLoading.current && !isLoading && response) {
      setMessages(prev => {
        // Replace the last assistant placeholder or add new
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.content === '...') {
          return [...prev.slice(0, -1), { role: 'assistant', content: response }];
        }
        return [...prev, { role: 'assistant', content: response }];
      });
    }
    prevIsLoading.current = isLoading;
  }, [isLoading, response]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && sessionStarted) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, sessionStarted]);

  const startSession = async () => {
    setSessionStarted(true);
    setMessages([{
      role: 'assistant',
      content: "Hello! I'm **AskWings AI Bot**, your technical assistant for Wings Bharath — Halamanee Aerospace Group.\n\nI can help you with:\n- Our manufacturing capabilities & certifications\n- Aerospace programmes (A320, Rafale, LCA Tejas, Su-30, UAVs)\n- Materials, tolerances & machining infrastructure\n- Quality standards (AS9100D, Airbus Approval)\n\nHow can I assist you today?"
    }]);

    // Notify team
    if (!notified) {
      setNotified(true);
      try {
        await fetch('/api/chat-session-notify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            visitorId: `session-${Date.now()}`,
            startedAt: new Date().toISOString(),
          }),
        });
      } catch {
        // Silent fail — notification is non-critical
      }
    }
  };

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    const userText = input.trim();
    setInput('');

    const newMessages: Message[] = [...messages, { role: 'user', content: userText }];
    setMessages([...newMessages, { role: 'assistant', content: '...' }]);

    const apiMessages = [
      { role: 'system' as const, content: WINGS_SYSTEM_PROMPT },
      ...newMessages.map(m => ({ role: m.role as 'user' | 'assistant', content: m.content })),
    ];

    sendMessage(apiMessages, { max_completion_tokens: 600 });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedQuestions = [
    'What certifications does Wings Bharath hold?',
    'What machining capabilities do you have?',
    'Which defence programmes have you worked on?',
    'What materials do you work with?',
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col" style={{ width: '380px', maxHeight: '600px' }}>
      {/* Chat Window */}
      <div className="flex flex-col bg-card border border-border rounded-2xl shadow-2xl overflow-hidden" style={{ height: '580px' }}>
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-primary via-secondary to-primary border-b border-accent/20 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 bg-accent/20 border border-accent/40 rounded-xl flex items-center justify-center">
                  <Icon name="CpuChipIcon" size={20} className="text-accent" />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-card rounded-full" />
              </div>
              <div>
                <h3 className="font-headline text-sm font-bold text-foreground">AskWings AI Bot</h3>
                <p className="text-xs text-accent font-cta tracking-wider">WINGS BHARATH TECHNICAL ASSISTANT</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {!sessionStarted ? (
          /* Pre-session screen */
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center space-y-5">
            <div className="w-20 h-20 bg-accent/10 border border-accent/30 rounded-2xl flex items-center justify-center">
              <Icon name="CpuChipIcon" size={40} className="text-accent" />
            </div>
            <div>
              <h3 className="font-headline text-xl font-bold text-foreground mb-2">AskWings AI Bot</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Get instant answers about Wings Bharath's aerospace manufacturing capabilities, certifications, and programmes.
              </p>
            </div>
            <div className="w-full space-y-2">
              {suggestedQuestions.slice(0, 2).map((q, i) => (
                <div key={i} className="px-3 py-2 bg-background border border-border rounded-lg text-xs text-muted-foreground text-left">
                  {q}
                </div>
              ))}
            </div>
            <button
              onClick={startSession}
              className="w-full px-6 py-3 bg-accent text-primary font-cta text-sm font-bold rounded-xl hover:bg-accent/90 transition-all duration-200 flex items-center justify-center space-x-2 shadow-glow"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={18} />
              <span>START TECHNICAL CHAT</span>
            </button>
            <p className="text-xs text-muted-foreground">Powered by OpenAI · Wings Bharath</p>
          </div>
        ) : (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 bg-accent/20 border border-accent/30 rounded-lg flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                      <Icon name="CpuChipIcon" size={14} className="text-accent" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] px-3 py-2.5 rounded-xl text-sm leading-relaxed ${
                      msg.role === 'user' ?'bg-accent text-primary font-medium rounded-tr-sm' :'bg-background border border-border text-foreground rounded-tl-sm'
                    }`}
                  >
                    {msg.content === '...' ? (
                      <div className="flex space-x-1 py-1">
                        <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap">{msg.content}</div>
                    )}
                  </div>
                </div>
              ))}

              {/* Live streaming indicator */}
              {isLoading && messages[messages.length - 1]?.content !== '...' && (
                <div className="flex justify-start">
                  <div className="w-7 h-7 bg-accent/20 border border-accent/30 rounded-lg flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                    <Icon name="CpuChipIcon" size={14} className="text-accent" />
                  </div>
                  <div className="max-w-[80%] px-3 py-2.5 rounded-xl bg-background border border-border text-foreground text-sm rounded-tl-sm">
                    <div className="whitespace-pre-wrap">{response || '...'}</div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Suggested questions (only at start) */}
            {messages.length === 1 && (
              <div className="flex-shrink-0 px-4 pb-2 flex flex-wrap gap-1.5">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setInput(q);
                      setTimeout(() => inputRef.current?.focus(), 50);
                    }}
                    className="px-2.5 py-1 bg-background border border-border text-xs text-muted-foreground rounded-full hover:border-accent hover:text-accent transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="flex-shrink-0 border-t border-border p-3">
              <div className="flex items-center space-x-2 bg-background border border-border rounded-xl px-3 py-2 focus-within:border-accent transition-colors">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about our capabilities..."
                  disabled={isLoading}
                  className="flex-1 bg-transparent text-sm text-foreground placeholder-muted-foreground focus:outline-none disabled:opacity-50"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center hover:bg-accent/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <Icon name="PaperAirplaneIcon" size={14} className="text-primary" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-1.5">
                AskWings AI · Wings Bharath Technical Assistant
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
